import { useState, useEffect } from "react";
import { ShieldCheck, Activity, AlertOctagon, RefreshCw, Cpu, ToggleLeft, ToggleRight } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface AutonomyStatus {
  status: "healthy" | "degraded";
  breakerTripped: boolean;
  rateLimitBreached: boolean;
  recentRunsCount: number;
  recentFailuresCount: number;
}

export function AutonomyDashboard() {
  const [status, setStatus] = useState<AutonomyStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [policyApproval, setPolicyApproval] = useState(true);
  const [policyRisk, setPolicyRisk] = useState("medium");

  const fetchAutonomyStatus = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:3001";
      const headers = { Authorization: `Bearer ${session?.access_token}` };

      const res = await fetch(`${apiUrl}/api/autonomy/status`, { headers });
      if (res.ok) {
        setStatus(await res.json());
      }
    } catch (e) {
      console.error("Failed to load autonomy status parameters", e);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePolicy = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:3001";
      
      const res = await fetch(`${apiUrl}/api/autonomy/policies`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          requiresApproval: policyApproval,
          maxAllowableRisk: policyRisk,
          forbidden: false,
          serviceId: "system_auto"
        })
      });

      if (res.ok) {
        toast.success("Autonomy safety policy updated successfully");
      } else {
        toast.error("Failed to update policy rules");
      }
    } catch {
      toast.error("Network communication failure updating policy");
    }
  };

  useEffect(() => {
    fetchAutonomyStatus();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <p className="text-sm font-black uppercase tracking-tight">Closed-Loop Autonomy Center</p>
          <p className="text-[10px] text-muted-foreground uppercase font-bold tracking-widest">
            Configure safety boundaries and monitor self-healing execution runs
          </p>
        </div>
        <button
          onClick={fetchAutonomyStatus}
          disabled={loading}
          className="h-8 px-3 rounded-none border-2 border-foreground bg-background font-black uppercase text-[9px] gap-1.5 flex items-center shadow-brutal-sm hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh Breakers
        </button>
      </div>

      {/* Circuit Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* System State */}
        <Card className={`border-2 border-foreground rounded-none shadow-brutal-sm ${
          status?.status === "healthy" ? "bg-status-up/5" : "bg-status-down/5"
        }`}>
          <CardContent className="p-4 flex items-center gap-3">
            <ShieldCheck className={`h-8 w-8 ${status?.status === "healthy" ? "text-status-up" : "text-status-down"}`} />
            <div>
              <p className="text-[9px] font-bold text-muted-foreground uppercase">Autonomy Mode Status</p>
              <p className="text-xs font-black uppercase">{status?.status || "Loading..."}</p>
            </div>
          </CardContent>
        </Card>

        {/* Circuit Breaker */}
        <Card className="border-2 border-foreground rounded-none shadow-brutal-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertOctagon className={`h-8 w-8 ${status?.breakerTripped ? "text-status-down animate-pulse" : "text-zinc-400"}`} />
            <div>
              <p className="text-[9px] font-bold text-muted-foreground uppercase">Circuit Breaker</p>
              <p className="text-xs font-black uppercase">
                {status?.breakerTripped ? "OPEN (Locked)" : "CLOSED (Safe)"}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Hourly Rate Limit */}
        <Card className="border-2 border-foreground rounded-none shadow-brutal-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <Activity className="h-8 w-8 text-primary" />
            <div>
              <p className="text-[9px] font-bold text-muted-foreground uppercase">Runs (Last Hour)</p>
              <p className="text-xs font-black uppercase">{status?.recentRunsCount || 0} / 3 Runs</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Safety Policy Config */}
      <Card className="border-2 border-foreground rounded-none shadow-brutal-sm">
        <CardHeader className="border-b-2 border-foreground bg-accent/5 p-4">
          <CardTitle className="text-xs font-black uppercase tracking-wider flex items-center gap-1.5">
            <Cpu className="h-4 w-4" /> Safety Boundary Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-4">
          {/* Toggle Rule */}
          <div className="flex items-center justify-between border-b border-foreground/10 pb-3">
            <div>
              <span className="text-[10px] font-black uppercase block">Human-in-the-Loop Gate</span>
              <span className="text-[8px] text-muted-foreground uppercase font-bold tracking-wider">
                Require operator confirmation for all production fixes
              </span>
            </div>
            <button onClick={() => setPolicyApproval(!policyApproval)}>
              {policyApproval ? (
                <ToggleRight className="h-8 w-8 text-primary" />
              ) : (
                <ToggleLeft className="h-8 w-8 text-muted-foreground" />
              )}
            </button>
          </div>

           {/* Risk Level Config */}
          <div className="flex items-center justify-between border-b border-foreground/10 pb-3">
            <div>
              <span className="text-[10px] font-black uppercase block">Maximum Allowable Execution Risk</span>
              <span className="text-[8px] text-muted-foreground uppercase font-bold tracking-wider">
                Reject mitigation runs automatically if calculated risk index exceeds limit
              </span>
            </div>
            <select
              value={policyRisk}
              onChange={(e) => setPolicyRisk(e.target.value)}
              className="h-8 rounded-none border-2 border-foreground bg-background px-2 font-mono text-[9px] font-bold uppercase shadow-brutal-sm outline-none"
            >
              <option value="low">Low Risk Only</option>
              <option value="medium">Medium & Low</option>
              <option value="high">Allow All Risks</option>
            </select>
          </div>

          {/* Trigger Threshold (Failed Pings) */}
          <div className="flex items-center justify-between border-b border-foreground/10 pb-3">
            <div>
              <span className="text-[10px] font-black uppercase block">Ping Failure Threshold</span>
              <span className="text-[8px] text-muted-foreground uppercase font-bold tracking-wider">
                Number of consecutive failed pings before triggering auto-recovery/rollbacks
              </span>
            </div>
            <select
              defaultValue="3"
              className="h-8 rounded-none border-2 border-foreground bg-background px-2 font-mono text-[9px] font-bold uppercase shadow-brutal-sm outline-none"
            >
              <option value="1">1 Fail (Aggressive)</option>
              <option value="3">3 Fails (Recommended)</option>
              <option value="5">5 Fails (Conservative)</option>
              <option value="10">10 Fails (High Latency)</option>
            </select>
          </div>

          {/* Recovery Automation Targets */}
          <div className="flex items-center justify-between border-b border-foreground/10 pb-3">
            <div>
              <span className="text-[10px] font-black uppercase block">Target Recovery Actions</span>
              <span className="text-[8px] text-muted-foreground uppercase font-bold tracking-wider">
                Allowed automated remedies when threshold breaches occur
              </span>
            </div>
            <div className="flex flex-col gap-1.5 items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" defaultChecked className="accent-primary" />
                <span className="text-[9px] font-mono font-bold uppercase">Vercel Auto-Rollback</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" defaultChecked className="accent-primary" />
                <span className="text-[9px] font-mono font-bold uppercase">Slack Notification Alert</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="accent-primary" />
                <span className="text-[9px] font-mono font-bold uppercase">Custom Webhook Trigger</span>
              </label>
            </div>
          </div>

          <div className="pt-2">
            <button
              onClick={handleSavePolicy}
              className="h-9 px-4 rounded-none border-2 border-foreground bg-foreground text-background font-black uppercase text-[10px] shadow-brutal-sm hover:bg-primary transition-all"
            >
              Save Autonomy Policy
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
